<template>
  <div class="rb">
    <!-- ═════════ operations toolbar: the day IS the title ═════════ -->
    <div class="rb-bar">
      <div class="rb-day">
        <button type="button" class="rb-day__nav" v-b-tooltip.hover :title="$t('roomBoard.tip.prevDay')"
                @click="shiftDate(-1)"><i class="fas fa-chevron-left"/></button>
        <div class="rb-day__center">
          <span class="rb-day__weekday">{{ date | moment('dddd') }}</span>
          <span class="rb-day__date tabular">{{ date | moment('DD MMMM YYYY') }}</span>
        </div>
        <button type="button" class="rb-day__nav" v-b-tooltip.hover :title="$t('roomBoard.tip.nextDay')"
                @click="shiftDate(1)"><i class="fas fa-chevron-right"/></button>
        <b-form-datepicker v-model="date" button-only button-variant="link" size="sm"
                           v-b-tooltip.hover :title="$t('roomBoard.tip.pickDate')"
                           :locale="$i18n.locale" class="rb-day__picker" @input="refresh"/>
        <button v-if="!isToday" type="button" class="rb-day__today" v-b-tooltip.hover :title="$t('roomBoard.tip.today')"
                @click="goToday">{{ $t('calendarPicker.today') }}</button>
      </div>

      <span class="rb-bar__spacer"/>

      <b-input-group size="sm" class="rb-search">
        <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
        <b-form-input v-model="search" :placeholder="$t('roomBoard.searchPlaceholder')" debounce="150"/>
      </b-input-group>
      <building-select-box v-model="buildings" :branch-ids="getSelectedBranchesIds" append-to-body
                           v-b-tooltip.hover :title="$t('roomBoard.tip.building')"
                           multiple clearable class="rb-building" @input="refresh"/>
      <div class="rb-tools">
        <button type="button" class="rb-tool" :class="{'rb-tool--on': grouped}"
                v-b-tooltip.hover :title="$t('roomBoard.tip.group')" @click="setGrouped(!grouped)">
          <i class="fa-solid fa-layer-group"/>
        </button>
        <button type="button" class="rb-tool" :class="{'rb-tool--on': compact}"
                v-b-tooltip.hover :title="$t('roomBoard.tip.compact')" @click="setCompact(!compact)">
          <i class="fa-solid fa-list"/>
        </button>
        <button type="button" class="rb-tool" v-b-tooltip.hover :title="$t('roomBoard.tip.reload')" @click="refresh">
          <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
        </button>
      </div>
    </div>

    <!-- ═════════ metrics ribbon: numbers, not boxes ═════════ -->
    <div class="rb-ops">
      <div class="rb-ops__occ" v-b-tooltip.hover :title="$t('roomBoard.tip.occupancy')">
        <span class="rb-ops__occ-pct tabular">{{ summary.occupancyPercent }}%</span>
        <span class="rb-ops__occ-meter"><i :style="{width: summary.occupancyPercent + '%'}"/></span>
        <span class="rb-ops__occ-sub tabular">{{ summary.occupied }} / {{ summary.totalRooms }}</span>
      </div>

      <button v-for="metric in metrics" :key="metric.key" type="button"
              class="rb-metric" :class="{'rb-metric--on': quickFilter === metric.filter, 'rb-metric--zero': metric.value === 0}"
              :disabled="metric.value === 0 && quickFilter !== metric.filter"
              v-b-tooltip.hover :title="metric.tip"
              @click="setQuickFilter(metric.filter)">
        <span class="rb-metric__value tabular" :style="metric.value > 0 && metric.tint ? {color: metric.tint} : null">{{ metric.value }}</span>
        <span class="rb-metric__label">{{ metric.label }}</span>
      </button>

      <span class="rb-ops__spacer"/>
      <span v-if="summary.revenueDay > 0" class="rb-ops__rev tabular">{{ summary.revenueDay | numberFormatter }} €</span>
    </div>

    <!-- ═════════ meta strip: result count + legend, nothing loud ═════════ -->
    <div class="rb-meta">
      <span class="rb-result tabular">{{ $t('roomBoard.showing', {shown: filteredRooms.length, total: rooms.length}) }}</span>
      <button v-if="quickFilter !== 'ALL' || search" type="button" class="rb-reset" @click="clearFilters">
        <i class="fas fa-times mr-25"/>{{ $t('Clear all') }}
      </button>
      <span class="rb-meta__spacer"/>
      <span class="rb-legend">
        <span><i style="background:#2fa46a"/>{{ $t('roomBoard.clean.CLEAN') }}</span>
        <span><i style="background:#e05252"/>{{ $t('roomBoard.clean.DIRTY') }}</span>
        <span><i style="background:#e8a23d"/>{{ $t('roomBoard.clean.IN_PROGRESS') }}</span>
      </span>
    </div>

    <!-- ═════════ the board ═════════ -->
    <div class="rb-layout" :class="{'rb-layout--open': selectedRoom != null}">
      <div class="rb-board">
        <div v-if="loading && !loaded" class="rb-grid" :class="{'rb-grid--compact': compact}">
          <b-skeleton v-for="n in 14" :key="n" class="rb-skeleton" animation="wave"/>
        </div>

        <template v-else>
          <div v-if="!filteredRooms.length" class="rb-empty">
            <i class="fa-solid fa-door-closed"/>
            <p>{{ rooms.length ? $t('roomBoard.noMatch') : $t('nodatafound') }}</p>
          </div>

          <template v-else>
            <!-- the board always splits by building; floors are optional sub-groups inside -->
            <section v-for="bldg in buildingGroups" :key="bldg.key" class="rb-bldg">
              <button v-if="showBuildingHeaders" type="button" class="rb-bldg__head" @click="toggleGroup(bldg.key)">
                <i class="fas mr-50" :class="collapsedGroups[bldg.key] ? 'fa-chevron-right' : 'fa-chevron-down'"/>
                <span class="rb-bldg__name">{{ bldg.label }}</span>
                <span class="rb-bldg__rule"/>
                <span class="rb-bldg__stat tabular">{{ occupiedIn(bldg.rooms) }} / {{ bldg.rooms.length }}</span>
              </button>

              <template v-if="!collapsedGroups[bldg.key]">
                <template v-if="grouped">
                  <section v-for="floor in floorsOf(bldg.rooms)" :key="bldg.key + floor.key" class="rb-floor">
                    <button type="button" class="rb-floor__head" @click="toggleGroup(bldg.key + floor.key)">
                      <i class="fas mr-50" :class="collapsedGroups[bldg.key + floor.key] ? 'fa-chevron-right' : 'fa-chevron-down'"/>
                      <span class="rb-floor__name">{{ floor.label }}</span>
                      <span class="rb-floor__rule"/>
                      <span class="rb-floor__stat tabular">{{ occupiedIn(floor.rooms) }} / {{ floor.rooms.length }}</span>
                    </button>
                    <div v-if="!collapsedGroups[bldg.key + floor.key]" class="rb-grid" :class="{'rb-grid--compact': compact, 'rb-stale': loading}">
                      <room-board-card v-for="room in floor.rooms" :key="room.roomId" :room="room" :compact="compact"
                                       :selected="selectedRoom && selectedRoom.roomId === room.roomId" @select="selectRoom"/>
                    </div>
                  </section>
                </template>

                <div v-else class="rb-grid" :class="{'rb-grid--compact': compact, 'rb-stale': loading}">
                  <room-board-card v-for="room in bldg.rooms" :key="room.roomId" :room="room" :compact="compact"
                                   :selected="selectedRoom && selectedRoom.roomId === room.roomId" @select="selectRoom"/>
                </div>
              </template>
            </section>
          </template>
        </template>
      </div>

      <div v-if="selectedRoom" class="rb-side">
        <room-board-drawer :room="selectedRoom" @close="selectedRoom = null"
                           @view-reservation="openReservation" @refresh="refresh"/>
      </div>
    </div>

    <contract-rent-sidebar ref="contractRentSidebar"/>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import BuildingSelectBox from '@/components/components/picker/buildingSelectBox.vue'
import RoomBoardCard from '@/components/panels/roomBoard/RoomBoardCard.vue'
import RoomBoardDrawer from '@/components/panels/roomBoard/RoomBoardDrawer.vue'
import ContractRentSidebar from '@/components/sidebar/ContractRent/ContractRentSidebar.vue'

const PREFS_KEY = 'crmRoomBoardPrefs'
const emptySummary = () => ({
  totalRooms: 0, occupied: 0, arrivals: 0, departures: 0, stayovers: 0, turnovers: 0,
  available: 0, availableReady: 0, availableDirty: 0, dirty: 0, cleaningInProgress: 0,
  maintenance: 0, openCleaningTasks: 0, attention: 0, revenueDay: 0, occupancyPercent: 0,
})

export default {
  name: 'RoomBoard',
  components: {BuildingSelectBox, RoomBoardCard, RoomBoardDrawer, ContractRentSidebar},
  data() {
    let prefs = {}
    try { prefs = JSON.parse(localStorage.getItem(PREFS_KEY)) || {} } catch (e) { /* defaults */ }
    return {
      date: this.$moment().format('YYYY-MM-DD'),
      buildings: [],
      search: '',
      quickFilter: 'ALL',
      compact: prefs.compact === true,
      grouped: prefs.grouped === true,
      collapsedGroups: {},
      loading: true,
      loaded: false,
      rooms: [],
      summary: emptySummary(),
      selectedRoom: null,
      listeners: {refresh: () => {}},
    }
  },
  created() {
    this.refresh()
    this.$root.$on('crm::branch-updated', (this.listeners.refresh = () => this.refresh()))
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.refresh)
  },
  computed: {
    ...mapGetters('data', ['getSelectedBranchesIds']),
    isToday() {
      return this.date === this.$moment().format('YYYY-MM-DD')
    },
    /**
     * The ONE filter surface: every count in the ribbon filters the board.
     * Tints repeat the exact colors the room tiles use for the same state.
     */
    metrics() {
      const summary = this.summary
      return [
        {key: 'inh', value: summary.occupied, tint: null, label: this.$t('roomBoard.filter.inHouse'), filter: 'OCCUPIED', tip: this.$t('roomBoard.tip.inHouse')},
        {key: 'arr', value: summary.arrivals, tint: '#3b6ff6', label: this.$t('roomBoard.kpi.arrivals'), filter: 'ARRIVALS', tip: this.$t('roomBoard.tip.arrivals')},
        {key: 'dep', value: summary.departures, tint: '#c07f14', label: this.$t('roomBoard.kpi.departures'), filter: 'DEPARTURES', tip: this.$t('roomBoard.tip.departures')},
        {key: 'turn', value: summary.turnovers, tint: '#8a5cf6', label: this.$t('roomBoard.kpi.turnovers'), filter: 'TURNOVERS', tip: this.$t('roomBoard.tip.turnovers')},
        {key: 'avail', value: summary.available, tint: null, label: this.$t('roomBoard.occ.AVAILABLE'), filter: 'AVAILABLE', tip: this.$t('roomBoard.tip.available')},
        {key: 'ready', value: summary.availableReady, tint: '#2fa46a', label: this.$t('roomBoard.kpi.ready'), filter: 'READY', tip: this.$t('roomBoard.tip.ready')},
        {key: 'dirty', value: summary.dirty, tint: '#e05252', label: this.$t('roomBoard.kpi.dirty'), filter: 'DIRTY', tip: this.$t('roomBoard.tip.dirty')},
        {key: 'cleaning', value: summary.cleaningInProgress, tint: '#e8a23d', label: this.$t('roomBoard.kpi.cleaning'), filter: 'CLEANING', tip: this.$t('roomBoard.tip.cleaning')},
        {key: 'maint', value: summary.maintenance, tint: '#8a93a5', label: this.$t('roomBoard.kpi.maintenance'), filter: 'MAINTENANCE', tip: this.$t('roomBoard.tip.maintenance')},
        {key: 'att', value: summary.attention, tint: '#e05252', label: this.$t('roomBoard.kpi.attention'), filter: 'ATTENTION', tip: this.$t('roomBoard.tip.attention')},
      ]
    },
    filteredRooms() {
      const term = (this.search || '').trim().toLowerCase()
      return this.rooms.filter(room => {
        if (!this.matches(room, this.quickFilter)) return false
        if (!term) return true
        const reservation = room.reservation || {}
        return [room.roomNumber, room.roomName, room.fullAddress, reservation.guestName,
          reservation.platformReservationId, reservation.contractId]
            .some(value => String(value || '').toLowerCase().includes(term))
      })
    },
    /** rooms arrive sorted by building, so insertion order keeps the groups sorted too */
    buildingGroups() {
      const groups = new Map()
      for (const room of this.filteredRooms) {
        const key = room.buildingId || room.buildingName || 'none'
        if (!groups.has(key)) {
          groups.set(key, {key: `b:${key}`, label: room.buildingName || this.$t('roomBoard.noBuilding'), rooms: []})
        }
        groups.get(key).rooms.push(room)
      }
      return Array.from(groups.values())
    },
    showBuildingHeaders() {
      return this.buildingGroups.length > 1
    },
  },
  methods: {
    ...mapActions('roomBoard', ['getBoard']),
    refresh() {
      this.loading = true
      this.getBoard({date: this.date, buildings: this.buildings})
          .then(data => {
            this.rooms = data.Rooms || []
            this.summary = {...emptySummary(), ...(data.Summary || {})}
            this.loaded = true
            if (this.selectedRoom) {
              this.selectedRoom = this.rooms.find(room => room.roomId === this.selectedRoom.roomId) || null
            }
          })
          .catch(() => { /* toast comes from the api layer; board keeps the last data */ })
          .finally(() => { this.loading = false })
    },
    shiftDate(days) {
      this.date = this.$moment(this.date).add(days, 'days').format('YYYY-MM-DD')
      this.refresh()
    },
    goToday() {
      this.date = this.$moment().format('YYYY-MM-DD')
      this.refresh()
    },
    matches(room, filterKey) {
      switch (filterKey) {
        case 'OCCUPIED': return ['OCCUPIED', 'ARRIVAL', 'TURNOVER'].includes(room.occupancy)
        case 'ARRIVALS': return ['ARRIVAL', 'TURNOVER'].includes(room.occupancy)
        case 'DEPARTURES': return ['DEPARTURE', 'TURNOVER'].includes(room.occupancy)
        case 'TURNOVERS': return room.occupancy === 'TURNOVER'
        case 'AVAILABLE': return room.occupancy === 'AVAILABLE'
        case 'READY': return room.occupancy === 'AVAILABLE' && room.cleaning === 'CLEAN'
        case 'DIRTY': return room.cleaning === 'DIRTY' || room.cleaning === 'OVERDUE'
        case 'CLEANING': return room.cleaning === 'IN_PROGRESS'
        case 'MAINTENANCE': return (room.damages || []).length > 0
        case 'ATTENTION': return (room.attention || []).length > 0
        default: return true
      }
    },
    setQuickFilter(key) {
      if (!key) return
      this.quickFilter = this.quickFilter === key ? 'ALL' : key
    },
    clearFilters() {
      this.quickFilter = 'ALL'
      this.search = ''
    },
    selectRoom(room) {
      this.selectedRoom = this.selectedRoom && this.selectedRoom.roomId === room.roomId ? null : room
    },
    openReservation(contractId) {
      if (contractId) this.$refs.contractRentSidebar.showContract(contractId)
    },
    toggleGroup(key) {
      this.$set(this.collapsedGroups, key, !this.collapsedGroups[key])
    },
    floorsOf(rooms) {
      const floors = new Map()
      for (const room of rooms) {
        const key = room.floorNumber != null ? String(room.floorNumber) : 'none'
        if (!floors.has(key)) floors.set(key, [])
        floors.get(key).push(room)
      }
      return Array.from(floors.entries())
          .sort((a, b) => {
            if (a[0] === 'none') return 1
            if (b[0] === 'none') return -1
            return Number(a[0]) - Number(b[0])
          })
          .map(([key, floorRooms]) => ({
            key: `|f:${key}`,
            label: key === 'none' ? this.$t('roomBoard.noFloor') : `${this.$t('roomBoard.floor')} ${key}`,
            rooms: floorRooms,
          }))
    },
    occupiedIn(rooms) {
      return rooms.filter(room => ['OCCUPIED', 'ARRIVAL', 'TURNOVER'].includes(room.occupancy)).length
    },
    setCompact(on) {
      this.compact = on
      this.savePrefs()
    },
    setGrouped(on) {
      this.grouped = on
      this.savePrefs()
    },
    savePrefs() {
      try { localStorage.setItem(PREFS_KEY, JSON.stringify({compact: this.compact, grouped: this.grouped})) } catch (e) { /* fine */ }
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/*
 * Command-center language: ONE flat page. Zones are separated by hairlines
 * and rhythm, not by boxes. Color only where it carries meaning.
 */

/* ── toolbar ── */
.rb-bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding-bottom: 12px; }
.rb-bar__spacer { flex: 1; }

.rb-day { display: flex; align-items: center; gap: 4px; }
.rb-day__nav {
  width: 30px; height: 30px; border: 1px solid rgba(23, 30, 48, .12); border-radius: 7px; cursor: pointer;
  background: transparent; color: #4a5468; font-size: .66rem;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all .12s;
  &:hover { border-color: #3b6ff6; color: #3b6ff6; }
  &:focus { outline: none; }
}
.rb-day__center { display: flex; flex-direction: column; align-items: center; min-width: 158px; padding: 0 6px; line-height: 1.12; }
.rb-day__weekday { font-size: .6rem; font-weight: 800; letter-spacing: .14em; text-transform: uppercase; color: #9aa2b1; }
.rb-day__date { font-size: 1.02rem; font-weight: 800; color: #171e30; letter-spacing: -.01em; white-space: nowrap; }
.rb-day__picker :deep(.btn) { padding: 2px 4px; color: #9aa2b1; &:hover { color: #3b6ff6; } }
.rb-day__today {
  border: 1px solid rgba(59, 111, 246, .35); background: transparent; color: #3b6ff6; border-radius: 7px;
  font-size: .62rem; font-weight: 800; letter-spacing: .08em; text-transform: uppercase;
  cursor: pointer; padding: 6px 11px; line-height: 1;
  transition: all .12s;
  &:hover { border-color: #3b6ff6; background: rgba(59, 111, 246, .07); }
  &:focus { outline: none; }
}

.rb-search { max-width: 230px; :deep(.form-control) { height: 30px; border-color: rgba(23, 30, 48, .12); } :deep(.input-group-text) { height: 30px; background: transparent; border-color: rgba(23, 30, 48, .12); color: #9aa2b1; font-size: .66rem; } }
.rb-building { min-width: 190px; max-width: 260px; :deep(.vs__dropdown-toggle) { min-height: 30px; border-color: rgba(23, 30, 48, .12); } }
.rb-tools { display: flex; align-items: center; gap: 5px; }
.rb-tool {
  width: 30px; height: 30px; border: 1px solid rgba(23, 30, 48, .12); border-radius: 7px; cursor: pointer;
  background: transparent; color: #7c8598; font-size: .7rem;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all .12s;
  &:hover { border-color: #3b6ff6; color: #3b6ff6; }
  &:focus { outline: none; }
  &--on { background: #171e30; border-color: #171e30; color: #fff; &:hover { color: #fff; } }
}

/* ── metrics ribbon ── */
.rb-ops {
  display: flex; align-items: center; flex-wrap: wrap;
  border-top: 1px solid rgba(23, 30, 48, .08);
  border-bottom: 1px solid rgba(23, 30, 48, .08);
  padding: 8px 0; margin-bottom: 10px; gap: 2px;
}
.rb-ops__spacer { flex: 1; }
.rb-ops__occ { display: flex; align-items: center; gap: 8px; padding-right: 16px; margin-right: 8px; border-right: 1px solid rgba(23, 30, 48, .08); }
.rb-ops__occ-pct { font-size: 1.05rem; font-weight: 800; color: #171e30; letter-spacing: -.01em; }
.rb-ops__occ-meter {
  width: 72px; height: 4px; border-radius: 2px; background: rgba(23, 30, 48, .08); overflow: hidden;
  i { display: block; height: 100%; background: #3b6ff6; border-radius: 2px; transition: width .4s ease; }
}
.rb-ops__occ-sub { font-size: .66rem; font-weight: 600; color: #9aa2b1; white-space: nowrap; }

.rb-metric {
  display: flex; align-items: baseline; gap: 6px;
  border: none; background: transparent; cursor: pointer;
  padding: 4px 11px; border-radius: 6px;
  transition: background .12s;
  &:hover { background: rgba(23, 30, 48, .05); }
  &:focus { outline: none; }
  &:disabled { cursor: default; &:hover { background: transparent; } }
  &--on { box-shadow: inset 0 -2px 0 #3b6ff6; .rb-metric__label { color: #171e30; } }
  &--zero { .rb-metric__value { color: #c3c9d4 !important; font-weight: 600; } }
}
.rb-metric__value { font-size: .92rem; font-weight: 800; color: #2a3348; }
.rb-metric__label { font-size: .62rem; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: #9aa2b1; white-space: nowrap; }
.rb-ops__rev { font-size: .86rem; font-weight: 800; color: #2a3348; padding-left: 12px; }

/* ── meta strip ── */
.rb-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 13px; }
.rb-meta__spacer { flex: 1; }
.rb-result { font-size: .68rem; font-weight: 600; color: #9aa2b1; white-space: nowrap; }
.rb-reset {
  border: none; background: transparent; cursor: pointer;
  color: #cc3f3f; font-size: .68rem; font-weight: 700; padding: 4px 8px; border-radius: 6px;
  &:hover { background: rgba(224, 82, 82, .08); }
  &:focus { outline: none; }
}
.rb-legend {
  display: flex; align-items: center; gap: 10px;
  span { display: inline-flex; align-items: center; gap: 4px; font-size: .62rem; font-weight: 600; color: #9aa2b1; }
  i { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
  @media (max-width: 991px) { display: none; }
}

/* ── board + drawer ── */
.rb-layout { display: grid; grid-template-columns: 1fr; gap: 18px; align-items: start; }
.rb-layout--open { grid-template-columns: minmax(0, 1fr) 344px; }
@media (max-width: 1100px) {
  .rb-layout--open { grid-template-columns: 1fr; }
  .rb-side { position: fixed; inset: 0; z-index: 1040; padding: 12px; background: rgba(10, 14, 30, .55); overflow-y: auto; }
}

.rb-grid { display: grid; gap: 9px; grid-template-columns: repeat(auto-fill, minmax(186px, 1fr)); }
.rb-grid--compact { grid-template-columns: 1fr; gap: 5px; }
.rb-stale { opacity: .55; pointer-events: none; transition: opacity .2s; }
.rb-skeleton { height: 92px; border-radius: 8px; margin: 0; }
.rb-grid--compact .rb-skeleton { height: 36px; }

.rb-bldg { margin-bottom: 20px; }
.rb-bldg__head {
  display: flex; align-items: center; gap: 8px; width: 100%;
  border: none; background: transparent; cursor: pointer; padding: 2px 0 9px;
  color: #7c8598; font-size: .64rem;
  &:focus { outline: none; }
  &:hover .rb-bldg__name { color: #3b6ff6; }
}
.rb-bldg__name { font-size: .72rem; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: #2a3348; transition: color .12s; }
.rb-bldg__rule { flex: 1; height: 1px; background: rgba(23, 30, 48, .09); }
.rb-bldg__stat { font-size: .66rem; font-weight: 700; color: #9aa2b1; }

.rb-floor { margin-bottom: 12px; }
.rb-floor__head {
  display: flex; align-items: center; gap: 8px; width: 100%;
  border: none; background: transparent; cursor: pointer; padding: 2px 0 8px;
  color: #7c8598; font-size: .64rem;
  &:focus { outline: none; }
  &:hover .rb-floor__name { color: #171e30; }
}
.rb-floor__name { font-size: .68rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; color: #7c8598; transition: color .12s; }
.rb-floor__rule { flex: 1; height: 1px; background: rgba(23, 30, 48, .07); }
.rb-floor__stat { font-size: .66rem; font-weight: 700; color: #9aa2b1; }

.rb-empty {
  text-align: center; color: #9aa2b1; padding: 70px 0;
  i { font-size: 2rem; opacity: .3; }
  p { margin-top: 10px; font-size: .84rem; }
}
</style>

<style lang="scss">
.dark-layout {
  .rb-day__date, .rb-ops__occ-pct { color: #eef1f7; }
  .rb-day__nav, .rb-tool { border-color: rgba(255, 255, 255, .14); color: #9aa2b1; }
  .rb-tool--on { background: #eef1f7; border-color: #eef1f7; color: #171e30; }
  .rb-ops { border-color: rgba(255, 255, 255, .08); }
  .rb-ops__occ { border-color: rgba(255, 255, 255, .08); }
  .rb-ops__occ-meter { background: rgba(255, 255, 255, .1); i { background: #5b8bff; } }
  .rb-metric__value { color: #cfd5e1; }
  .rb-metric:hover { background: rgba(255, 255, 255, .05); }
  .rb-metric--on { box-shadow: inset 0 -2px 0 #5b8bff; .rb-metric__label { color: #eef1f7; } }
  .rb-day__today { border-color: rgba(123, 163, 255, .4); color: #7ba3ff; &:hover { border-color: #7ba3ff; background: rgba(123, 163, 255, .08); } }
  .rb-metric--zero .rb-metric__value { color: #5b6478 !important; }
  .rb-ops__rev { color: #cfd5e1; }
  .rb-metric:disabled:hover { background: transparent; }
  .rb-bldg__name { color: #cfd5e1; }
  .rb-bldg__rule { background: rgba(255, 255, 255, .1); }
  .rb-floor__rule { background: rgba(255, 255, 255, .08); }
  .rb-search .input-group-text, .rb-search .form-control { border-color: rgba(255, 255, 255, .14); }
  .rb-skeleton { background: #232b42; }
  .rb-empty { color: #676d7d; }
}
</style>
